import { Checkbox, CheckboxField, Combobox, ComboboxOption, type IComboboxGroup, Label, SegmentedButton, SegmentedButtonSegment, Textarea } from '@d-edge/overnight-hotelier-react'
import { useEffect, useMemo, useState } from 'react'
import { useQueueMessageTypes } from '@/api/queries'
import type { QueueMessageType } from '@/api/types'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Modal } from '@/components/ui/Modal'
import { useToast } from '@/components/ui/Toast'
import { useDebouncedValue } from '@/lib/hooks'
import {
  decodeQueueMessage,
  encodeQueueMessage,
  findMessageType,
  MAX_MESSAGE_BYTES,
  payloadForType,
  type DecodedMessage,
  type EnvelopeFormat,
  type MessageDraft,
} from './queueMessage'
import styles from './QueueMessageDialog.module.css'

type Mode = 'decompress' | 'compress'
type Outcome<T> = { ok: true; value: T } | { ok: false; error: string }

const EMPTY_DRAFT: MessageDraft = { format: 'queue-extensions', type: '', executionAssemblyName: null, payload: '' }

/** Runs `run` on the debounced key; results are keyed by it so a stale run never shows against newer input. */
function useDebouncedRun<K, T>(key: K, enabled: boolean, run: (key: K) => Promise<T>): Outcome<T> | null {
  const debounced = useDebouncedValue(key, 250)
  const [state, setState] = useState<{ key: K; outcome: Outcome<T> } | null>(null)

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    run(debounced).then(
      (value) => !cancelled && setState({ key: debounced, outcome: { ok: true, value } }),
      (error: unknown) => !cancelled && setState({ key: debounced, outcome: { ok: false, error: error instanceof Error ? error.message : String(error) } }),
    )
    return () => {
      cancelled = true
    }
  }, [debounced, enabled, run])

  return enabled && state?.key === key ? state.outcome : null
}

function useCopy() {
  const toast = useToast()
  return (text: string) =>
    navigator.clipboard.writeText(text).then(
      () => toast.notify('Message copied'),
      (error: unknown) => toast.error(error, 'Copy failed'),
    )
}

/** Decodes and builds queue messages in the browser, so payloads never leave it. */
export function QueueMessageDialog({ onClose }: { onClose: () => void }) {
  const [mode, setMode] = useState<Mode>('decompress')
  const [draft, setDraft] = useState<MessageDraft>(EMPTY_DRAFT)

  const modes = (
    <SegmentedButton name="queue-message-mode" aria-label="Direction" value={mode} onChange={(value) => setMode(value as Mode)}>
      <SegmentedButtonSegment value="decompress">Decompress</SegmentedButtonSegment>
      <SegmentedButtonSegment value="compress">Compress</SegmentedButtonSegment>
    </SegmentedButton>
  )

  return (
    <Modal title="Queue message" onClose={onClose} size="xl" headerExtra={modes}>
      {mode === 'decompress' ? (
        <DecompressPane
          onEdit={(next) => {
            setDraft(next)
            setMode('compress')
          }}
        />
      ) : (
        <CompressPane draft={draft} onChange={setDraft} />
      )}
    </Modal>
  )
}

function describe(message: DecodedMessage): string {
  const content = message.isJson ? 'JSON' : 'text (not JSON)'
  return message.compressed ? `GZip ${content}` : `Uncompressed base64 ${content}`
}

function DecompressPane({ onEdit }: { onEdit: (draft: MessageDraft) => void }) {
  const [input, setInput] = useState('')
  const [showEnvelope, setShowEnvelope] = useState(false)
  const result = useDebouncedRun(input, input.trim() !== '', decodeQueueMessage)
  const copy = useCopy()
  const message = result?.ok ? result.value : null
  const output = message && (showEnvelope && message.envelope ? message.envelope : message.text)

  return (
    <>
      <Textarea
        fullWidth
        resize="vertical"
        className={`u-mono ${styles.input}`}
        placeholder="Paste a message from Storage Explorer or the Azure portal (base64 GZip, starts with H4sI)"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        spellCheck={false}
        aria-label="Compressed queue message"
      />
      <div className={styles.bar}>
        {!result && <span className={styles.summary}>Paste a message to decode it.</span>}
        {result && !result.ok && (
          <span className={styles.error} role="alert">
            {result.error}
          </span>
        )}
        {message && (
          <span className={styles.meta}>
            {message.messageType && <Badge tone="info">{message.messageType}</Badge>}
            <span className={styles.summary}>{describe(message)}</span>
          </span>
        )}
        {message && (
          <span className={styles.meta}>
            {message.envelope && (
              <CheckboxField>
                <Checkbox checked={showEnvelope} onChange={(e) => setShowEnvelope(e.target.checked)} />
                <Label>show envelope</Label>
              </CheckboxField>
            )}
            <Button size="sm" onClick={() => onEdit(message.draft)}>
              Edit and compress
            </Button>
            <Button size="sm" onClick={() => copy(output!)}>
              Copy
            </Button>
          </span>
        )}
      </div>
      {output && <pre className={styles.output}>{output}</pre>}
    </>
  )
}

type TypeOption = QueueMessageType & { value: string }

function groupByNamespace(types: readonly QueueMessageType[]): IComboboxGroup<TypeOption>[] {
  const groups = new Map<string, TypeOption[]>()
  for (const type of types) groups.set(type.namespace, [...(groups.get(type.namespace) ?? []), { ...type, value: type.type }])
  return [...groups].sort(([a], [b]) => a.localeCompare(b)).map(([label, options]) => ({ label, options }))
}

function CompressPane({ draft, onChange }: { draft: MessageDraft; onChange: (draft: MessageDraft) => void }) {
  const result = useDebouncedRun(draft, draft.payload.trim() !== '', encodeQueueMessage)
  const copy = useCopy()
  const update = (patch: Partial<MessageDraft>) => onChange({ ...draft, ...patch })
  const { data: types, isError } = useQueueMessageTypes()
  const groups = useMemo(() => groupByNamespace(types ?? []), [types])
  const known = findMessageType(types ?? [], draft.type)
  const typeHint = known
    ? `${known.name} from DEdge.RateScreener.Core.Contracts`
    : draft.type.trim()
      ? 'Typed by hand: not in the RateScreener contracts, sent as written'
      : isError
        ? 'The RateScreener contracts could not be loaded; type the name by hand'
        : 'Receivers match on the class name; the assembly part is optional'
  const encoded = result?.ok ? result.value : null
  const tooBig = encoded !== null && encoded.length > MAX_MESSAGE_BYTES

  return (
    <>
      <div className={styles.fields}>
        <Field label="Envelope" hint={draft.format === 'queue-extensions' ? 'type, executionAssemblyName, object · most DI services' : draft.format === 'functions' ? 'Type, Object · Availpro.Azure.Functions senders' : 'The payload alone'}>
          <SegmentedButton name="queue-message-envelope" aria-label="Envelope" value={draft.format} onChange={(value) => update({ format: value as EnvelopeFormat })}>
            <SegmentedButtonSegment value="queue-extensions">QueueExtensions</SegmentedButtonSegment>
            <SegmentedButtonSegment value="functions">Functions</SegmentedButtonSegment>
            <SegmentedButtonSegment value="none">None</SegmentedButtonSegment>
          </SegmentedButton>
        </Field>
        {draft.format !== 'none' && (
          <Field label="Message type" hint={typeHint} className={styles.grow}>
            <Combobox<TypeOption>
              fullWidth
              options={groups}
              value={draft.type}
              onInputChange={(type) => update({ type, payload: payloadForType(types ?? [], draft, type) })}
              renderOption={(option) => (
                <ComboboxOption key={option.value} value={option.value}>
                  {option.name}
                </ComboboxOption>
              )}
              placeholder="Pick a RateScreener contract or type any assembly-qualified name"
              noResultsLabel={(input) => `No contract matches "${input}"; it is sent as typed.`}
              clearLabel="Clear the message type"
            />
          </Field>
        )}
      </div>
      <Textarea
        fullWidth
        resize="vertical"
        className={`u-mono ${styles.input}`}
        placeholder='Message payload, e.g. {"hotelId": 42}'
        value={draft.payload}
        onChange={(e) => update({ payload: e.target.value })}
        spellCheck={false}
        aria-label="Message payload"
      />
      <div className={styles.bar}>
        {!result && <span className={styles.summary}>Write the payload to build the message.</span>}
        {result && !result.ok && (
          <span className={styles.error} role="alert">
            {result.error}
          </span>
        )}
        {encoded !== null && (
          <span className={tooBig ? styles.error : styles.summary} role={tooBig ? 'alert' : undefined}>
            {(encoded.length / 1024).toFixed(1)} KB of 64 KB{tooBig && ' · too big for a storage queue'}
          </span>
        )}
        {encoded !== null && (
          <Button size="sm" onClick={() => copy(encoded)}>
            Copy
          </Button>
        )}
      </div>
      {encoded !== null && <pre className={styles.output}>{encoded}</pre>}
    </>
  )
}
